import React, { useEffect, useState, useRef, lazy, memo, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, ShieldCheckIcon, LockIcon, ZapIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
let icFiredVInt = false;
const trackVInt = (name: string, price: number) => {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'AddToCart', {
    content_name: name,
    value: price,
    currency: 'USD'
  });
  if (!icFiredVInt) {
    icFiredVInt = true;
    window.fbq('track', 'InitiateCheckout', {
      content_name: name,
      value: price,
      currency: 'USD'
    });
  }
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const ACCENT = '#FF6C00';
const ACCENT_GLOW = 'rgba(255,108,0,0.3)';
const BG = '#0B0B0B';
const GOLD = '#D4AF37';
// ─── Checkout URLs ────────────────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://library.legendaryreads.com/cf/click/1';
const CHECKOUT_BUNDLE = 'https://library.legendaryreads.com/cf/click/2';
const BASE_PRICE = 29.97;
const BUMP1_PRICE = 30;
const BUMP1_ITEMS = ['Back In Time — 40th Anniversary Edition (190 pages)', 'Behind The Clock Tower — Stories From The Set (87 pages)', 'Great Scott! 40 Quotes For 40 Years (87 pages)', 'Time. Imagination. Destiny. — The Philosophical Legacy (71 pages)', 'Instant Download — All 4 Books'];
// ─── Shared primitives ────────────────────────────────────────────────────────
const OrangeButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  small?: boolean;
}) => <a href={href} onClick={onClick} className="cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg" style={{
  minHeight: small ? '44px' : '56px',
  padding: small ? '10px 16px' : '16px 24px',
  fontSize: small ? '14px' : '17px',
  background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
  color: '#0B0B0B',
  fontFamily: 'Inter, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}} data-id="element-1945">
    {children}
  </a>;
const BumpItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-2" data-id="element-1946">
    <span style={{
    color: ACCENT,
    flexShrink: 0,
    fontSize: '9px',
    marginTop: '5px'
  }} data-id="element-1947">
      ✦
    </span>
    <span className="text-xs leading-snug" style={{
    color: 'rgba(245,245,245,0.78)',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-1948">
      {text}
    </span>
  </li>;
// ─── Image Lightbox ───────────────────────────────────────────────────────────
const ImageLightbox = ({
  images,
  startIndex,
  onClose
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => i === 0 ? images.length - 1 : i - 1);
  const next = () => setIdx(i => i === images.length - 1 ? 0 : i + 1);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-1949">
      <div className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-1950">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close" data-id="element-1951">
          <XIcon className="h-7 w-7" data-id="element-1952" />
        </button>
        <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{
        aspectRatio: '1/1'
      }} data-id="element-1953">
          <img src={images[idx]} alt={`Item ${idx + 1}`} className="w-full h-full object-contain" decoding="async" loading="lazy" data-id="element-1954" />
        </div>
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Previous" data-id="element-1955">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-1956" />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Next" data-id="element-1957">
          <ChevronRightIcon className="h-5 w-5" data-id="element-1958" />
        </button>
        <div className="flex justify-center gap-1.5 mt-3" data-id="element-1959">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className="rounded-full transition-all duration-200" style={{
          width: i === idx ? '20px' : '8px',
          height: '8px',
          backgroundColor: i === idx ? ACCENT : 'rgba(255,255,255,0.3)'
        }} data-id="element-1960" />)}
        </div>
      </div>
    </div>;
};
// ─── HEADER ──────────────────────────────────────────────────────────────────
const HeaderVInt = () => <header className="bg-black text-white border-b border-orange-500/30 z-40 sticky top-0" data-id="element-1961">
    <div className="container mx-auto px-4 py-2.5 flex justify-between items-center" data-id="element-1962">
      <span className="font-bold text-lg bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text font-future" data-id="element-1963">
        Back In Time
      </span>
      <a href="#pricing-section" className="cta font-bold text-sm px-4 py-1.5 rounded-md transition-all hover:brightness-110" style={{
      background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
      color: '#0B0B0B',
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1964">
        Get My Copy
      </a>
    </div>
  </header>;
// ─── HERO SECTION ─────────────────────────────────────────────────────────
const HeroVInt = () => <section className="py-6 md:py-10 relative overflow-hidden" style={{
  backgroundColor: BG
}} data-id="element-1965">
    <div className="absolute inset-0 opacity-20" style={{
    background: 'radial-gradient(ellipse at 70% 50%, rgba(255,108,0,0.15) 0%, transparent 70%)'
  }} data-id="element-1966" />
    <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10" data-id="element-1967">
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-1968">
        {/* Left: copy */}
        <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-1969">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.12em'
        }} data-id="element-1970">
            40th Anniversary Collector's Edition
          </p>
          <h1 className="text-[28px] md:text-5xl font-bold leading-tight mb-4 text-white font-future tracking-tight" data-id="element-1971">
            You've Seen It 50 Times.{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1972">
              You've Never Read Anything Like This.
            </span>
          </h1>
          <p className="text-sm md:text-lg text-white/75 leading-relaxed mb-5 max-w-xl mx-auto md:mx-0" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1973">
            The only collector's edition that goes beyond the film — into the
            production truth, the cultural legacy, and the reason this story
            lives in your bones 40 years later. Available in Digital. Yours to
            keep permanently.
          </p>

          {/* Mobile image */}
          <div className="md:hidden mb-5 flex justify-center" data-id="element-1974">
            <div className="relative w-[85%]" data-id="element-1975">
              <div className="absolute -inset-1 rounded-xl blur-md opacity-40" style={{
              background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
            }} data-id="element-1976" />
              <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
              aspectRatio: '1/1'
            }} data-id="element-1977">
                <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="400" height="400" data-id="element-1978" />
              </div>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2 justify-center md:justify-start mb-5" data-id="element-1979">
            <div className="flex gap-0.5" data-id="element-1980">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1981" />)}
            </div>
            <span className="text-white font-bold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1982">
              4.9
            </span>
            <span className="text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1983">
              (5,354+ Verified Reviews)
            </span>
          </div>

          <div className="max-w-md mx-auto md:mx-0" data-id="element-1984">
            <OrangeButton href="#pricing-section" data-id="element-1985">
              GET MY COPY NOW →
            </OrangeButton>
          </div>

          <div className="flex items-center justify-center md:justify-start gap-4 mt-4" data-id="element-1986">
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1987">
              <ShieldCheckIcon className="h-4 w-4 text-green-400" data-id="element-1988" />
              Secure Checkout
            </div>
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1989">
              <ZapIcon className="h-4 w-4 text-yellow-400" data-id="element-1990" />
              Instant Access
            </div>
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1991">
              <LockIcon className="h-4 w-4 text-orange-400" data-id="element-1992" />
              One-Time Payment
            </div>
          </div>
        </div>

        {/* Right: desktop image */}
        <div className="hidden md:flex md:w-1/2 justify-center items-center" data-id="element-1993">
          <div className="relative w-full max-w-md" data-id="element-1994">
            <div className="absolute -inset-2 rounded-2xl blur-xl opacity-30" style={{
            background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
          }} data-id="element-1995" />
            <div className="relative rounded-2xl overflow-hidden border border-orange-500/30 shadow-[0_0_60px_rgba(255,108,0,0.2)]" style={{
            aspectRatio: '1/1'
          }} data-id="element-1996">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="600" height="600" data-id="element-1997" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── IDENTIFICATION BLOCK ─────────────────────────────────────────────────
const IdentificationVInt = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1998">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-1999">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 font-future tracking-wide" data-id="element-2000">
        This Is For You If...
      </h2>

      <div className="space-y-4 mb-8" data-id="element-2001">
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-2002">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-2003">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2004">
            You still get chills when the lightning hits the clock tower — and
            you've never been able to fully explain why
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-2005">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-2006">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2007">
            You've rewatched it more times than you can count, and it still hits
            differently than anything made in the last 40 years
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-2008">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-2009">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2010">
            You've tried to put into words what made this film different — and
            realized no words were ever quite enough
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-2011">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-2012">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2013">
            You feel like something that genuinely shaped who you are deserves
            more than a Wikipedia page and a streaming algorithm
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-2014">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-2015">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2016">
            You want to finally understand — not just feel — why this story
            refuses to leave you alone
          </p>
        </div>
      </div>

      <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8 text-center italic" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-2017">
        Most anniversary products treat you like a casual viewer. This one was
        built for the fan who already loves the film — and is ready to finally
        understand it.
      </p>

      <div className="text-center mb-8" data-id="element-2018">
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{
        color: ACCENT,
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-2019">
          INTRODUCING
        </p>
        <h3 className="text-2xl md:text-3xl font-bold mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-2020">
          BACK IN TIME
        </h3>
        <p className="text-white font-semibold text-lg md:text-xl mb-3" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-2021">
          The 40th Anniversary Collector's Edition
        </p>
        <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-2022">
          The most complete legacy document ever assembled for Back to the
          Future.
        </p>
      </div>

      <div className="flex justify-center" data-id="element-2023">
        <div className="relative w-full max-w-md" data-id="element-2024">
          <div className="absolute -inset-1 rounded-xl blur-md opacity-30" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-2025" />
          <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
          aspectRatio: '1/1'
        }} data-id="element-2026">
            <img src="https://cdn.magicpatterns.com/uploads/3vn3oGbmC4DQi9DBZ99M7S/BACK_IN_TIME_-_DIGITAL11v2.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" loading="lazy" decoding="async" width="500" height="500" data-id="element-2027" />
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── WHAT YOU GET STACK ────────────────────────────────────────────────────
const BOOK_IMAGES = ['https://cdn.magicpatterns.com/uploads/eRaLLFj1Xpd9yKh17SfpDw/BACK_IN_TIME_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/qWMGMZni8tSubnCQBBHeda/BEHIND_THE_CLOCK_TOWER_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/6N2oU2pwJLDZiEUA7vpeCu/GREAT_SCOTT_40_QUOTES_FOR_40_YEARS_COVER_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/8pvDLEzUAHUVNEPcPZcf74/TIME_IMAGINATION_DESTINY_-_COVER_DIGITAL.png'];
const BookImage = ({
  src,
  alt,
  onClick
}: {
  src: string;
  alt: string;
  onClick: () => void;
}) => <div className="flex-shrink-0 w-full md:w-[160px]" data-id="element-2028">
    <div className="relative cursor-pointer group mx-auto max-w-full md:max-w-[160px]" onClick={onClick} data-id="element-2029">
      <img src={src} alt={alt} className="rounded-lg w-full object-cover shadow-lg transition-opacity group-hover:opacity-80" style={{
      aspectRatio: '3/4'
    }} loading="lazy" decoding="async" data-id="element-2030" />
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-2031">
        <div className="bg-black/50 rounded-full p-1.5" data-id="element-2032">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-2033">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-2034" />
          </svg>
        </div>
      </div>
      <p className="text-center text-[9px] mt-1 leading-tight" style={{
      color: ACCENT,
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-2035">
        Tap to enlarge
      </p>
    </div>
  </div>;
const WhatYouGetVInt = () => {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-2036">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-2037">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-2038">
          What Makes This Different
        </h2>
        <p className="text-center text-white/50 text-sm mb-8" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-2039">
          Four volumes. Every angle. Nothing left out.
        </p>

        <div className="space-y-6" data-id="element-2040">
          {/* Point 1 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-2041">
            <BookImage src={BOOK_IMAGES[0]} alt="Back In Time — The Cultural Analysis" onClick={() => setLightboxImage(BOOK_IMAGES[0])} data-id="element-2042" />
            <div className="flex-1" data-id="element-2043">
              <div className="flex items-center gap-2 mb-2" data-id="element-2044">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2045">
                  BOOK 1
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-2046">
                IT DOCUMENTS THE LEGACY — NOT THE PLOT
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2047">
                190+ pages covering the cultural architecture of a story that's
                outlasted every trend, every franchise reboot, and every piece
                of technology it predicted. Why does it still feel alive? Why do
                complete strangers cry in the same place every time? Why do
                parents all over the world sit their kids down and press play
                like it's a rite of passage? This book answers all of it — with
                the depth the film has always deserved.
              </p>
            </div>
          </div>

          {/* Point 2 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-2048">
            <BookImage src={BOOK_IMAGES[1]} alt="Behind The Clock Tower" onClick={() => setLightboxImage(BOOK_IMAGES[1])} data-id="element-2049" />
            <div className="flex-1" data-id="element-2050">
              <div className="flex flex-col items-start gap-1 mb-2" data-id="element-2051">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2052">
                  BOOK 2
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-2053">
                THE PRODUCTION TRUTH NOBODY PUBLISHED PROPERLY
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2054">
                Michael J. Fox working 18-hour days — Family Ties from 10 AM to
                6 PM, then the BTTF set until 2 AM. The DeLorean that kept
                breaking down on night shoots, requiring industrial hairdryers
                to unfreeze its gull-wing doors between takes. The casting
                decision that nearly bankrupted the production and cost $4
                million to fix. It's all here, documented in full.
              </p>
            </div>
          </div>

          {/* Point 3 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-2055">
            <BookImage src={BOOK_IMAGES[2]} alt="Great Scott!" onClick={() => setLightboxImage(BOOK_IMAGES[2])} data-id="element-2056" />
            <div className="flex-1" data-id="element-2057">
              <div className="flex flex-col items-start gap-1 mb-2" data-id="element-2058">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2059">
                  BOOK 3
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-2060">
                40 VOICES. 40 YEARS. EVERY ONE COUNTS.
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2061">
                Not just the famous quotes. The layered ones. The ones that hit
                completely differently on the 20th viewing than they did on the
                first. Each one contextualized, with the real-world resonance
                documented behind it. The book you read once, then return to.
              </p>
            </div>
          </div>

          {/* Point 4 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-2062">
            <BookImage src={BOOK_IMAGES[3]} alt="Time. Imagination. Destiny." onClick={() => setLightboxImage(BOOK_IMAGES[3])} data-id="element-2063" />
            <div className="flex-1" data-id="element-2064">
              <div className="flex flex-col items-start gap-1 mb-2" data-id="element-2065">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2066">
                  BOOK 4
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-2067">
                THE PHILOSOPHY THAT EXPLAINS WHY IT STILL HAUNTS YOU
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2068">
                Why does a movie about a teenager in a time machine still feel
                this important 40 years later? This volume goes deeper than any
                film review ever has — exploring what Back to the Future says
                about free will, identity, ambition, and the courage required to
                change your future. The one you won't stop thinking about after
                you read it.
              </p>
            </div>
          </div>
        </div>
      </div>

      {lightboxImage && <ImageLightbox images={[lightboxImage]} startIndex={0} onClose={() => setLightboxImage(null)} data-id="element-2069" />}
    </section>;
};
// ─── PRICING + ORDER BUMP ─────────────────────────────────────────────────────
const PurchaseVInt = () => {
  const [bump1, setBump1] = useState(false);
  const [bump1LightboxOpen, setBump1LightboxOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const hasTrackedRef = useRef(false);
  const total = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0);
  const checkoutUrl = bump1 ? CHECKOUT_BUNDLE : CHECKOUT_DIGITAL;
  const handleCta = () => {
    const name = bump1 ? 'Complete Digital Collection' : 'Digital Edition';
    trackVInt(name, total);
  };
  const scrollToCta = () => {
    if (window.innerWidth < 768 && ctaRef.current) {
      setTimeout(() => {
        ctaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 50);
    }
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current && !sessionStorage.getItem('pricing_section_viewed')) {
          hasTrackedRef.current = true;
          sessionStorage.setItem('pricing_section_viewed', 'true');
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} id="pricing-section" style={{
    backgroundColor: BG,
    paddingTop: '32px',
    paddingBottom: '48px',
    scrollMarginTop: '80px'
  }} className="md:py-12" data-id="element-2070">
      <div className="container mx-auto px-4 md:px-10 max-w-2xl" data-id="element-2071">
        <div className="text-center mb-6 md:mb-10" data-id="element-2072">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3" style={{
          backgroundColor: 'rgba(255,108,0,0.15)',
          color: ACCENT,
          border: '1px solid rgba(255,108,0,0.3)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2073">
            🔥 40th Anniversary Launch Pricing
          </span>
          <h2 className="text-[22px] md:text-4xl font-bold text-center font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-2074">
            Special 40th Anniversary Collector's Offer
          </h2>
          <p className="text-white/50 text-xs mt-2 max-w-sm mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2075">
            ⚠️ This price exists because it's the 40th anniversary. When the
            window closes, it closes.
          </p>
        </div>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)] overflow-hidden" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-2076">
          <div className="p-6 md:p-10" data-id="element-2077">
            <div className="flex justify-center mb-6" data-id="element-2078">
              <img src="https://cdn.magicpatterns.com/uploads/3vn3oGbmC4DQi9DBZ99M7S/BACK_IN_TIME_-_DIGITAL11v2.png" alt="Digital Edition" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" width="260" height="260" data-id="element-2079" />
            </div>

            <div className="text-center mb-2" data-id="element-2080">
              <span className="line-through opacity-40 mr-2 text-lg" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2081">
                USD $50.00
              </span>
              <span className="font-bold text-3xl transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2082">
                USD ${total.toFixed(2)}
              </span>
            </div>

            <div className="text-center mb-6" data-id="element-2083">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-bold px-3 py-1.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2084">
                You Save USD ${(50.0 + (bump1 ? 60.0 : 0) - total).toFixed(2)}
              </span>
            </div>

            <div ref={ctaRef} data-id="element-2085">
              <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-2086">
                {bump1 ? 'GET MY COLLECTION →' : 'GET MY COPY →'}
              </OrangeButton>
            </div>
            <p className="text-center text-white/40 text-xs mt-3" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-2087">
              Instant delivery. Readable on any device.
            </p>
            {/* Trust icons */}
            <div className="flex items-center justify-center gap-5 mt-4" data-id="element-2088">
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2089">
                <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-2090" /> Secure
                Checkout
              </div>
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2091">
                <LockIcon className="h-4 w-4 text-orange-400" data-id="element-2092" /> One-Time
                Payment
              </div>
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 1 ── */}
        <div className="rounded-xl mb-3 overflow-hidden transition-all duration-300" style={{
        border: bump1 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump1 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump1 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-2093">
          <div className="px-4 py-2" style={{
          backgroundColor: bump1 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-2094">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-2095">
              ⚡ Special One-Time Offer
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-2096">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const n = !bump1;
            setBump1(n);
            if (n) scrollToCta();
          }} data-id="element-2097">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump1 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-2098">
                {bump1 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-2099" />}
              </div>
              <div data-id="element-2100">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2101">
                  Add The Complete Digital Collection{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2102">
                    — Only +USD ${BUMP1_PRICE} Today
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2103">
                  Get all 4 volumes. The complete archive.
                </p>
                <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2 py-1 rounded-full mt-1.5" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2104">
                  You Save USD $30
                </span>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-3" data-id="element-2105">
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setBump1LightboxOpen(true);
            }} data-id="element-2106">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-2107">
                  <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-lg shadow-lg w-full h-full object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" width="130" height="130" data-id="element-2108" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-2109">
                    <div className="bg-black/50 rounded-full p-1.5" data-id="element-2110">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-2111">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-2112" />
                      </svg>
                    </div>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2113">
                  Tap to enlarge
                </p>
              </div>
              <ul className="space-y-1.5 flex-1" data-id="element-2114">
                {BUMP1_ITEMS.map(item => <BumpItem key={item} text={item} data-id="element-2115" />)}
              </ul>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump1 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const n = !bump1;
            setBump1(n);
            if (n) scrollToCta();
          }} data-id="element-2116">
              {bump1 ? '✓ Added to my order' : `Add Complete Collection — Only +USD ${BUMP1_PRICE} (Normally USD $60)`}
            </div>
          </div>
        </div>
      </div>

      {bump1LightboxOpen && <ImageLightbox images={['https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png']} startIndex={0} onClose={() => setBump1LightboxOpen(false)} data-id="element-2117" />}
    </section>;
};
// ─── SOCIAL PROOF ─────────────────────────────────────────────────────────
const TESTIMONIALS_DATA = [{
  quote: "Finally. Something that actually respects the legacy. I've bought anniversary merch before. Keychains, Blu-rays with the same bonus features recycled every five years. This is different. Back In Time reads like it was written by someone who genuinely understands what this film meant — not just as entertainment, but as a cultural moment. I'm 52 years old and I still got chills reading the chapter on the clock tower sequence. Worth every penny.",
  name: 'David M., 52',
  location: 'Software Engineer | Portland, Oregon, USA'
}, {
  quote: "I cried reading 'Behind the Clock Tower.' Actual tears. I wasn't expecting to be emotional about a book on filmmaking. But when they described Michael J. Fox working 18-hour days between sets, the dedication these people had — it hit me hard. My dad took me to see this film in 1985. He passed three years ago. Reading this felt like honoring both of them.",
  name: 'Karen T., 58',
  location: 'Retired Teacher | Manchester, UK'
}, {
  quote: "My 16-year-old watched it with me after I showed her this collection. I've been trying to get my daughter to understand why Back to the Future isn't just 'an old movie.' After she read a few sections of Time. Imagination. Destiny. with me, she watched the trilogy start to finish. Unprompted. That's the power of this collection — it gives you the language to explain why this film matters to people who weren't there.",
  name: 'James R., 47',
  location: 'Architect | Toronto, Canada'
}, {
  quote: "The best anniversary release in a decade. No contest. I've been a BTTF fan my entire adult life and I'm deeply skeptical of anniversary marketing. Cash grabs everywhere. This is not that. Back In Time is genuinely researched, intelligently written, and emotionally honest. The philosophical analysis in Volume 4 alone is worth the price of the entire collection.",
  name: 'Michael B., 61',
  location: 'University Lecturer | Edinburgh, Scotland'
}, {
  quote: "Read it in one sitting. Couldn't stop. I downloaded the digital collection on a Friday night thinking I'd skim the first volume. Looked up at 2am and I was halfway through Volume 3. The production stories in Behind the Clock Tower are absolutely riveting — details I'd never read anywhere else. The DeLorean stories alone are worth it.",
  name: 'Rachel H., 44',
  location: 'Marketing Director | Sydney, Australia'
}, {
  quote: "This is the document I didn't know I needed. There's something strange about loving a film for 40 years and never being able to fully explain why. This collection gave me the vocabulary. The chapter on why the film resonates across generations isn't just insightful — it's validating. Like someone finally said out loud what we all felt but couldn't articulate.",
  name: 'Thomas W., 55',
  location: 'Small Business Owner | Chicago, Illinois, USA'
}, {
  quote: "Gifted the hardcover to my brother. He called me the next day. My brother and I saw Back to the Future together when we were kids. We both remembered exactly where we were sitting in the theater. I ordered the hardcover upgrade as his birthday gift, and he called me the day it arrived, genuinely emotional. We ended up talking for two hours about the film. Best gift I've given in years.",
  name: 'Sandra L., 50',
  location: 'Registered Nurse | Calgary, Canada'
}, {
  quote: "Skeptical at first. Completely wrong to be. I'll be honest — I almost didn't buy this because I've been burned before by anniversary content that turned out to be repackaged Wikipedia articles. This is not that. The writing is sharp, the research is deep, and the emotional framing throughout is genuine. Whoever wrote this actually loves the film.",
  name: 'Andrew F., 48',
  location: 'IT Project Manager | Melbourne, Australia'
}, {
  quote: "The section on the Eric Stoltz recasting alone is worth it. I'd heard the basic story before, but the level of detail in Behind the Clock Tower goes far beyond anything I'd read. The production decisions that almost killed the movie, the pressure on everyone involved — it reads like a thriller. And then the film comes out and becomes one of the greatest of all time. Remarkable story.",
  name: 'Christopher D., 53',
  location: 'Film Professor | Boston, Massachusetts, USA'
}, {
  quote: "I've rewatched the trilogy 20+ times. I learned things I never knew. That should tell you something about the depth of this collection. These aren't surface-level fun facts. The production history, the cultural analysis, the philosophical chapters — it's a serious, respectful treatment of a film that deserves exactly that. I'll be reading this again.",
  name: 'Fiona M., 46',
  location: 'Graphic Designer | Dublin, Ireland'
}, {
  quote: "My husband and I read it together. Best purchase we've made in months. We both grew up on this film but from different countries — he's American, I'm British. It was fascinating to compare how differently we each experienced the film culturally, and then read how universally it landed worldwide. Time. Imagination. Destiny. made us both feel seen in a way we didn't expect.",
  name: 'Patricia & Alan G., 56 & 59',
  location: 'Retired Educators | Phoenix, Arizona, USA'
}, {
  quote: "I was worried this would be AI-generated filler. It absolutely is not. That concern crossed my mind when I first saw it — I'm in my 50s, not naive. But two pages in, it was obvious this was written by people who know the film deeply and care about doing it justice. The specificity of the production details, the quality of the cultural analysis — this is real work. Real love for the film.",
  name: 'Robert K., 57',
  location: 'Civil Engineer | Denver, Colorado, USA'
}, {
  quote: "The 40 quotes in 'Great Scott!' made me laugh and cry on the same page. That's an achievement. Volume 3 is the emotional heart of the collection for me. Forty perspectives on forty years — some funny, some devastating, some deeply philosophical. The range of voices is incredible. I read it in one afternoon and felt strangely lighter afterward. Like something had been resolved.",
  name: 'Diane A., 62',
  location: 'Librarian | Auckland, New Zealand'
}, {
  quote: "This is what I wanted to give my son on his 18th birthday. He'd never seen Back to the Future. I sat him down, we watched all three films, and then I handed him the digital collection. He spent the next week reading it between classes. He texted me yesterday: 'Dad, I finally get why you love this movie so much.' That's the whole point, isn't it?",
  name: 'Mark S., 51',
  location: 'Sales Manager | Houston, Texas, USA'
}, {
  quote: "40 years later, this film still has more to say. So does this collection. I was 15 when I first saw it in the cinema. I'm 55 now. And reading Back In Time reminded me that genuine art doesn't expire — it deepens. This collection doesn't just celebrate the film. It argues for why it matters. And it makes that argument beautifully. I'm grateful someone did this properly.",
  name: 'Jonathan P., 55',
  location: 'Writer | Vancouver, Canada'
}, {
  quote: "I've collected BTTF merchandise for 20 years. Steelbooks, prints, the whole thing. This is the first time I've read something about this film that made me feel like the person who made it actually understood why it matters. Read the whole flagship book in one night.",
  name: 'Mark T., 47',
  location: 'Columbus, Ohio'
}, {
  quote: "My son is 13. He'd seen the trilogy twice but didn't really get why I treat it like it's sacred. I read him the section on why fathers show this film to their kids. He went quiet for a second and then said 'that's literally us right now, Dad.' That's worth more than any price tag.",
  name: 'Sandra R., 52',
  location: 'Austin, Texas'
}, {
  quote: "I went in skeptical, every 'definitive' BTTF product I've bought has been gorgeous packaging with nothing underneath. Behind The Clock Tower alone destroyed that pattern. The DeLorean hairdryer story, the Stoltz reshoot, the 18-hour Fox schedule, I've been a fan for 35 years and I didn't know any of it at this level of detail.",
  name: 'David M., 39',
  location: 'San Diego, California'
}];
const TestimonialsVInt = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleEnd = () => {
    setIsDragging(false);
  };
  // Duplicate the array for seamless infinite scroll
  const duplicatedTestimonials = [...TESTIMONIALS_DATA, ...TESTIMONIALS_DATA];
  return <section className="py-8 md:py-12 overflow-hidden" style={{
    backgroundColor: BG
  }} data-id="element-2118">
      <div className="mb-8 px-4" data-id="element-2119">
        <h2 className="text-2xl md:text-3xl font-bold text-center font-future tracking-wide text-white" data-id="element-2120">
          What Real Fans Are Saying
        </h2>
      </div>

      <div className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing" onMouseDown={handleMouseDown} onMouseLeave={handleEnd} onMouseUp={handleEnd} onMouseMove={handleMouseMove} onTouchStart={handleTouchStart} onTouchEnd={handleEnd} onTouchMove={handleTouchMove} data-id="element-2121">
        {/* Gradient masks for smooth edges */}
        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 z-10 pointer-events-none bg-gradient-to-r from-[#0B0B0B] to-transparent" data-id="element-2122" />
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 z-10 pointer-events-none bg-gradient-to-l from-[#0B0B0B] to-transparent" data-id="element-2123" />

        <div ref={scrollRef} className="flex overflow-x-hidden no-scrollbar" data-id="element-2124">
          <div className={`flex gap-5 w-max ${isDragging ? '' : 'animate-marquee'}`} data-id="element-2125">
            {duplicatedTestimonials.map((testimonial, index) => <div key={index} className="p-5 rounded-xl flex flex-col flex-shrink-0 w-[320px] md:w-[350px] select-none" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(255,108,0,0.15)'
          }} data-id="element-2126">
                <div className="flex gap-0.5 mb-3" data-id="element-2127">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-2128" />)}
                </div>
                <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-2129">
                  "{testimonial.quote}"
                </p>
                <div data-id="element-2130">
                  <p className="text-white font-semibold text-sm" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2131">
                    {testimonial.name}
                  </p>
                  <p className="text-white/40 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-2132">
                    {testimonial.location}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// ─── FAQ ───────────────────────
const faqItemsVInt = [{
  question: 'What format is the book available in?',
  answer: 'The book is available in digital format (PDF, instant download). It is compatible with all devices, phones, tablets, computers, and e-readers.'
}, {
  question: 'How quickly will I receive my order?',
  answer: 'Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.'
}, {
  question: 'Is this official/licensed content?',
  answer: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  question: 'What makes this different from other BTTF books?',
  answer: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  question: 'Can I gift this to someone?',
  answer: 'Yes. The download link is sent to the email used at checkout, you can forward it to the recipient or purchase using their email address.'
}, {
  question: 'Are there future updates or additions?',
  answer: 'Digital edition buyers may receive updates if new content is added to the collection. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  question: 'Who wrote this book?',
  answer: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material, it was created intentionally for true fans.'
}, {
  question: 'Is this book generated entirely by AI?',
  answer: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  question: 'What if I have an issue with my order?',
  answer: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
const FAQItemVInt = ({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => <div className="border rounded-lg overflow-hidden transition-colors duration-200" style={{
  borderColor: 'rgba(255,108,0,0.2)'
}} data-id="element-2133">
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 cursor-pointer" style={{
    backgroundColor: 'rgba(17,17,17,0.8)'
  }} aria-expanded={isOpen} data-id="element-2134">
      <span className="text-base font-semibold text-white pr-4" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-2135">
        {question}
      </span>
      <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{
      color: ACCENT
    }} data-id="element-2136" />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-2137">
      <div className="px-5 pb-5 pt-1" style={{
      backgroundColor: 'rgba(11,11,11,0.6)'
    }} data-id="element-2138">
        {answer.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm leading-relaxed mb-3 last:mb-0" style={{
        color: 'rgba(245,245,245,0.6)',
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-2139">
            {paragraph}
          </p>)}
      </div>
    </div>
  </div>;
const FAQVInt = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-2140">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-2141">
        <div className="text-center mb-8" data-id="element-2142">
          <h2 className="text-2xl md:text-3xl font-bold font-future text-white mb-2" data-id="element-2143">
            Questions? We've Got Answers.
          </h2>
          <div className="w-20 h-1 mx-auto rounded-full" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-2144" />
        </div>
        <div className="space-y-3" data-id="element-2145">
          {faqItemsVInt.map((item, index) => <FAQItemVInt key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-2146" />)}
        </div>
      </div>
    </section>;
};
// ─── SOCIAL PROOF NOTIFICATION ────────────────────────────────────────────────
const firstNames = ['James', 'Michael', 'Robert', 'John', 'David', 'William', 'Richard', 'Joseph', 'Thomas', 'Christopher', 'Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen'];
const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Thompson', 'White', 'Harris', 'Clark', 'Lewis'];
const tier1Cities = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'London, UK', 'Manchester, UK', 'Toronto, ON', 'Vancouver, BC', 'Sydney, NSW', 'Melbourne, VIC'];
const products = [{
  name: 'Complete Digital Edition',
  price: 'USD $29.97',
  image: 'https://cdn.magicpatterns.com/uploads/3vn3oGbmC4DQi9DBZ99M7S/BACK_IN_TIME_-_DIGITAL11v2.png'
}, {
  name: 'Complete Digital Collection',
  price: 'USD $59.97',
  image: 'https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png'
}];
const SocialProofNotificationVInt = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const [userLocation, setUserLocation] = useState<{
    city: string;
    country: string;
  } | null>(null);
  useEffect(() => {
    // Fetch user location once
    fetch('https://ipapi.co/json/').then(res => res.json()).then(data => {
      if (data.city && data.country_name) {
        setUserLocation({
          city: data.city,
          country: data.country_name
        });
      }
    }).catch(() => {}); // Silently fail and fallback to tier1
  }, []);
  const generateRandomNotification = () => {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const product = products[Math.floor(Math.random() * products.length)];
    let locationStr = '';
    const useLocal = Math.random() < 0.75;
    if (useLocal && userLocation) {
      // 75% chance to use user's country/region
      locationStr = `${userLocation.city}, ${userLocation.country}`;
    } else {
      // 25% chance (or fallback) to use Tier 1 cities
      locationStr = tier1Cities[Math.floor(Math.random() * tier1Cities.length)];
    }
    return {
      name: `${firstName} ${lastName}`,
      city: locationStr,
      product: product.name,
      price: product.price,
      image: product.image
    };
  };
  useEffect(() => {
    const getRandomInterval = () => Math.floor(Math.random() * 2000) + 12000; // 12-14 seconds
    const showNotification = () => {
      setCurrentNotification(generateRandomNotification());
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
    };
    const initialDelay = setTimeout(() => {
      showNotification();
    }, 12000);
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, getRandomInterval());
    };
    scheduleNext();
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timeoutId);
    };
  }, [userLocation]); // Re-run when location is fetched
  if (!currentNotification) return null;
  return <div className={`fixed z-30 transition-all duration-500 ease-out
        bottom-4 left-1/2 -translate-x-1/2
        md:bottom-5 md:left-5 md:translate-x-0
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`} data-id="element-2147">
      <div className="relative backdrop-blur-md rounded-lg border shadow-[0_4px_20px_rgba(0,0,0,0.5)] p-3 flex items-center gap-3 w-[260px] sm:w-[280px]" style={{
      backgroundColor: 'rgba(11,11,11,0.95)',
      borderColor: 'rgba(255,108,0,0.3)'
    }} data-id="element-2148">
        <button onClick={() => setIsVisible(false)} className="absolute top-1.5 right-1.5 transition-colors z-10" style={{
        color: 'rgba(245,245,245,0.6)'
      }} aria-label="Close notification" data-id="element-2149">
          <XIcon className="h-3 w-3" data-id="element-2150" />
        </button>

        <div className="flex-shrink-0" data-id="element-2151">
          <div className="w-12 h-12 rounded-md overflow-hidden border border-orange-500/20" data-id="element-2152">
            <img src={currentNotification.image} alt="Book" className="w-full h-full object-cover" data-id="element-2153" />
          </div>
        </div>

        <div className="flex-1 min-w-0 pr-4" data-id="element-2154">
          <div className="flex items-center gap-1 mb-0.5" data-id="element-2155">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-id="element-2156"></div>
            <span className="text-[10px] font-semibold text-green-400 uppercase tracking-wide" data-id="element-2157">
              Just Purchased
            </span>
          </div>
          <p className="text-xs font-semibold text-white truncate" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2158">
            {currentNotification.name}
          </p>
          <p className="text-[10px] truncate" style={{
          color: 'rgba(245,245,245,0.6)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2159">
            {currentNotification.city}
          </p>
          <p className="text-[10px] font-semibold mt-0.5" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-2160">
            {currentNotification.product}
          </p>
        </div>
      </div>
    </div>;
};
// ─── LANDING VINT EXPORT ────────────────────────────────────────────────────────
export const LandingVInt = () => <div className="w-full min-h-screen" style={{
  backgroundColor: BG,
  color: '#F5F5F5'
}} data-id="element-2161">
    <HeaderVInt data-id="element-2162" />
    <main data-id="element-2163">
      <HeroVInt data-id="element-2164" />
      <IdentificationVInt data-id="element-2165" />
      <WhatYouGetVInt data-id="element-2166" />
      <TestimonialsVInt data-id="element-2167" />
      <PurchaseVInt data-id="element-2168" />
      <FAQVInt data-id="element-2169" />
    </main>
    <Footer data-id="element-2170" />
    <SocialProofNotificationVInt data-id="element-2171" />
  </div>;